import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Image,
  ImageSourcePropType,
  Platform,
  ScrollView,
  StatusBar,
  StyleProp,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';

import TabBar from '../../components/TabBar';
import PINConfirmModal from '../../components/UIComponents/PINConfirmModal';
import { usePIN } from '../../contexts/PINContext';
import { useSnackbar } from '../../contexts/SnackbarContext';
import {
  CardApplicationConflictError,
  InvestmentRequestConflictError,
  v2CreateCardApplication,
  v2CreateInvestmentRequest,
  v2GetDepositProducts,
  v2GetMyBalances,
  v2GetMyPushNotifications,
  v2GetMyRequests,
} from '../../services/v2/api';
import type { V2DepositProduct } from '../../types/v2';
import { CARD_SURFACE, COLORS } from '../../theme/themeTokens';
import { useIdempotencyKey } from '../../hooks/useIdempotencyKey';

// Products screen (Figma "Products"): Card / Deposit / T-FUND / Personal
// tabs. Card, T-FUND and Personal send a request after the PIN; Deposit
// opens the deposit form.

type ProductTab = 'card' | 'deposit' | 't_fund' | 'personal';
/** What a request button creates — also the request type it shows up as. */
type RequestKind = 'open_card' | 'open_t_fund' | 'personal_investment';

const TABS: readonly (readonly [ProductTab, string])[] = [
  ['card', 'Card'],
  ['deposit', 'Deposit'],
  ['t_fund', 'T-FUND'],
  ['personal', 'Personal'],
];

const BANNERS: Record<ProductTab, ImageSourcePropType> = {
  card: require('../../assets/product_card.png'),
  deposit: require('../../assets/product_deposit.png'),
  t_fund: require('../../assets/product_t-fund.png'),
  personal: require('../../assets/product_personal_invest.png'),
};

const CARD_FEATURES: { label: string; value?: string; icon: ImageSourcePropType }[] = [
  { label: 'Payments online and offline', icon: require('../../assets/percent.png') },
  { label: 'Limit per transaction', value: '50 000 €', icon: require('../../assets/receipt.png') },
  { label: 'Monthly limit on the card', value: '100 000 €', icon: require('../../assets/creditcard_white.png') },
];

const DEPOSIT_TERMS = [3, 6, 12] as const;
const DEPOSIT_CURRENCIES = ['USDT', 'BTC', 'ETH'] as const;
// Marketing figure from the design: the T-FUND product sits outside the
// catalogue the app can read (Extra group, no term).
const T_FUND_APY = '15%';

const REQUEST_TITLES: Record<RequestKind, string> = {
  open_card: 'Confirm card application',
  open_t_fund: 'Confirm T-FUND request',
  personal_investment: 'Confirm Personal investment request',
};

/** "3.0%" from a fraction string; "—" when there is no rate. */
export const formatRate = (rate: string | null | undefined): string => {
  const value = Number(rate);
  if (rate == null || rate === '' || !Number.isFinite(value)) { return '—'; }
  return `${(value * 100).toFixed(1)}%`;
};

/** Rate per term × currency from the Base deposit catalogue. */
export const buildRateTable = (products: V2DepositProduct[]) => {
  const table: Record<number, Record<string, string>> = {};
  for (const term of DEPOSIT_TERMS) {
    table[term] = {};
    for (const currency of DEPOSIT_CURRENCIES) {
      const row = products.find((p) =>
        p.term === term &&
        p.currency === currency &&
        (!p.product_type_name || p.product_type_name === 'Deposit'),
      );
      table[term][currency] = formatRate(row?.rate);
    }
  }
  return table;
};

interface ProductsScreenProps {
  onNavigate: (screen: string) => void;
  /** Opens the deposit form (Deposit tab button). */
  onOpenDeposit: () => void;
}

// Web: no extra 20 — the title sits at the height of the other screens' (Home, Card details, …).
const statusBarHeight = Platform.OS === 'android' ? 28 : Platform.OS === 'web' ? 0 : 20;

const Banner: React.FC<{
  source: ImageSourcePropType;
  title: string;
  subtitle: string;
  /** Widens or moves the text block when the artwork leaves room. */
  textStyle?: StyleProp<ViewStyle>;
  children?: React.ReactNode;
}> = ({ source, title, subtitle, textStyle, children }) => {
  const resolved = Image.resolveAssetSource(source as number);
  const aspectRatio = resolved?.width && resolved?.height ? resolved.width / resolved.height : 2.1;
  return (
    <View style={[styles.banner, { aspectRatio }]}>
      <Image source={source} style={styles.bannerImage} resizeMode="cover" />
      <View style={[styles.bannerText, textStyle]} pointerEvents="none">
        <Text style={styles.bannerTitle}>{title}</Text>
        <Text style={styles.bannerSubtitle}>{subtitle}</Text>
      </View>
      {children}
    </View>
  );
};

const PrimaryButton: React.FC<{ label: string; onPress: () => void; disabled?: boolean; busy?: boolean }> = ({
  label, onPress, disabled, busy,
}) => (
  <TouchableOpacity
    style={[styles.button, (disabled || busy) && styles.buttonDisabled]}
    onPress={onPress}
    disabled={disabled || busy}
    activeOpacity={0.85}
    accessibilityRole="button"
  >
    {busy ? <ActivityIndicator color={COLORS.white} /> : <Text style={styles.buttonText}>{label}</Text>}
  </TouchableOpacity>
);

const ProductsScreen: React.FC<ProductsScreenProps> = ({ onNavigate, onOpenDeposit }) => {
  const { isPINSetup, isPINSkipped } = usePIN();
  // One attempt per request: a press after a lost answer resends it with the
  // same key, and the server does not file it twice.
  const idempotency = useIdempotencyKey<{ kind: RequestKind }>();
  const snackbar = useSnackbar();

  const [activeTab, setActiveTab] = useState<ProductTab>('card');
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);
  // null while unknown: the Apply button waits rather than flashing.
  const [hasCard, setHasCard] = useState<boolean | null>(null);
  const [inProgress, setInProgress] = useState<Set<RequestKind>>(new Set());
  const [products, setProducts] = useState<V2DepositProduct[]>([]);
  const [pinFor, setPinFor] = useState<RequestKind | null>(null);
  const [sending, setSending] = useState<RequestKind | null>(null);

  const loadRequests = useCallback(async () => {
    try {
      const res = await v2GetMyRequests();
      const active = new Set<RequestKind>();
      for (const r of res.requests ?? []) {
        const kind = r.type_code as RequestKind;
        if (kind in REQUEST_TITLES && (r.status_code === 'pending' || r.status_code === 'in_process')) {
          active.add(kind);
        }
      }
      setInProgress(active);
    } catch {
      // Without the list the buttons stay available; the backend still
      // refuses a duplicate with 409.
    }
  }, []);

  useEffect(() => {
    v2GetMyPushNotifications()
      .then((res) => setHasUnreadNotifications((res.notifications || []).some((n: any) => !n.viewed_at)))
      .catch(() => {});
    v2GetMyBalances()
      .then((res) => setHasCard((res.balances || []).some(
        (b) => b.account_type_code === 'card' || b.account_type_code === 'card_balance',
      )))
      .catch(() => {});
    v2GetDepositProducts()
      .then((res) => setProducts(res.products ?? []))
      .catch(() => setProducts([]));
    loadRequests();
  }, [loadRequests]);

  const rates = useMemo(() => buildRateTable(products), [products]);

  const markInProgress = (kind: RequestKind) =>
    setInProgress((prev) => new Set(prev).add(kind));

  const sendRequest = async (kind: RequestKind) => {
    setPinFor(null);
    setSending(kind);
    try {
      const attempt = idempotency.attempt({ kind });
      if (kind === 'open_card') {
        await v2CreateCardApplication(attempt.key);
      } else {
        await v2CreateInvestmentRequest(kind === 'open_t_fund' ? 't_fund' : 'personal_investment', attempt.key);
      }
      idempotency.answered();
      markInProgress(kind);
      snackbar.show({ message: 'Request sent' });
    } catch (err) {
      idempotency.failed(err);
      if (err instanceof InvestmentRequestConflictError ||
          (err instanceof CardApplicationConflictError && err.kind === 'active_application_exists')) {
        markInProgress(kind);
        snackbar.show({ message: 'Your request is already in progress' });
      } else if (err instanceof CardApplicationConflictError) {
        setHasCard(true);
        snackbar.show({ message: 'You already have a card' });
      } else {
        const message = err instanceof Error ? err.message : 'Could not send the request.';
        Alert.alert('Request not sent', message, [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Retry', onPress: () => { sendRequest(kind); } },
        ]);
      }
    } finally {
      setSending(null);
    }
  };

  // PIN first, like other money-related confirmations; without a PIN set up
  // (or skipped) the request goes straight away.
  const requestWithPin = (kind: RequestKind) => {
    if (!isPINSetup || isPINSkipped) {
      sendRequest(kind);
    } else {
      setPinFor(kind);
    }
  };

  const requestButton = (kind: RequestKind, label: string) => (
    inProgress.has(kind)
      ? <PrimaryButton label="Request in progress" onPress={() => {}} disabled />
      : <PrimaryButton label={label} onPress={() => requestWithPin(kind)} busy={sending === kind} />
  );

  return (
    <View style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor={COLORS.primaryNavy} />

      <View style={[styles.header, { paddingTop: statusBarHeight }]}>
        <View style={styles.headerRow}>
          <Text style={styles.headerTitle}>Products</Text>
          <TouchableOpacity
            onPress={() => onNavigate('notifications')}
            style={styles.bellButton}
            accessibilityRole="button"
            accessibilityLabel="Notifications"
          >
            <Image source={require('../../assets/bell.png')} style={styles.bell} resizeMode="contain" />
            {hasUnreadNotifications && <View style={styles.bellDot} />}
          </TouchableOpacity>
        </View>
        <View style={styles.tabStrip} accessibilityRole="tablist">
          {TABS.map(([tab, label]) => {
            const selected = tab === activeTab;
            return (
              <TouchableOpacity
                key={tab}
                style={styles.tabButton}
                onPress={() => setActiveTab(tab)}
                activeOpacity={0.7}
                accessibilityRole="tab"
                accessibilityState={{ selected }}
              >
                <Text style={[styles.tabLabel, selected && styles.tabLabelActive]}>{label}</Text>
                <View style={[styles.tabUnderline, selected && styles.tabUnderlineActive]} />
              </TouchableOpacity>
            );
          })}
        </View>
      </View>

      <View style={styles.contentWrapper}>
        <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
          {activeTab === 'card' && (
            <>
              <Banner source={BANNERS.card} title="Card" subtitle={'For online transactions\nand daily operations.'} />
              <View style={styles.infoCard}>
                {CARD_FEATURES.map((row, idx) => (
                  <View key={row.label} style={[styles.featureRow, idx < CARD_FEATURES.length - 1 && styles.rowDivider]}>
                    <Image source={row.icon} style={styles.featureIcon} resizeMode="contain" />
                    <Text style={styles.featureLabel}>{row.label}</Text>
                    {!!row.value && <Text style={styles.featureValue}>{row.value}</Text>}
                  </View>
                ))}
              </View>
              {hasCard === false && requestButton('open_card', 'Apply card')}
            </>
          )}

          {activeTab === 'deposit' && (
            <>
              <Banner source={BANNERS.deposit} title="Deposit" subtitle={'Fixed-term rates with\nincreased yield.'} />
              <View style={styles.infoCard}>
                <View style={[styles.rateRow, styles.rowDivider]}>
                  <Text style={[styles.rateTerm, styles.rateHead]}>Term</Text>
                  {DEPOSIT_CURRENCIES.map((c) => (
                    <Text key={c} style={[styles.rateCell, styles.rateHead]}>{c}</Text>
                  ))}
                </View>
                {DEPOSIT_TERMS.map((term, idx) => (
                  <View key={term} style={[styles.rateRow, idx < DEPOSIT_TERMS.length - 1 && styles.rowDivider]}>
                    <Text style={styles.rateTerm}>{`${term} month`}</Text>
                    {DEPOSIT_CURRENCIES.map((c) => (
                      <Text key={c} style={styles.rateCell}>{rates[term][c]}</Text>
                    ))}
                  </View>
                ))}
              </View>
              <PrimaryButton label="Open deposit" onPress={onOpenDeposit} />
            </>
          )}

          {activeTab === 't_fund' && (
            <>
              <Banner
                source={BANNERS.t_fund}
                title="T-FUND"
                subtitle={'Profit maximization using DeFi\ninvestment instruments and AI\nfor investors willing to accept\nrisk in pursuit of superior returns.'}
                textStyle={styles.bannerTextWide}
              >
                <View style={styles.apyBadge} pointerEvents="none">
                  <Text style={styles.apyCurrency}>USDT</Text>
                  <Text style={styles.apyValue}>{`APY ${T_FUND_APY}`}</Text>
                </View>
                <Text style={styles.disclaimer} pointerEvents="none">
                  {'* Investment strategies\ninvolve risk of capital loss.'}
                </Text>
              </Banner>
              {requestButton('open_t_fund', 'Send request')}
            </>
          )}

          {activeTab === 'personal' && (
            <>
              <Banner
                source={BANNERS.personal}
                title="Personal Investments"
                subtitle={'Personalized investment\nsolutions available upon request.'}
              />
              {requestButton('personal_investment', 'Send request')}
            </>
          )}
        </ScrollView>
      </View>

      <TabBar activeTab="products" onNavigate={onNavigate} />

      <PINConfirmModal
        visible={pinFor !== null}
        title={pinFor ? REQUEST_TITLES[pinFor] : ''}
        subtitle="Your request will be sent to T-ONE"
        onSuccess={() => { if (pinFor) { sendRequest(pinFor); } }}
        onCancel={() => setPinFor(null)}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: COLORS.primaryNavy },
  header: { backgroundColor: COLORS.primaryNavy },
  headerRow: {
    height: 44,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerTitle: { fontFamily: 'Inter', fontSize: 20, fontWeight: '500', color: COLORS.white },
  bellButton: { width: 40, height: 40, alignItems: 'flex-end', justifyContent: 'center' },
  bell: { width: 26, height: 26, tintColor: COLORS.white },
  bellDot: {
    position: 'absolute',
    top: 7,
    right: 0,
    width: 9,
    height: 9,
    borderRadius: 5,
    backgroundColor: '#FF3B30',
    borderWidth: 1.5,
    borderColor: COLORS.primaryNavy,
  },
  // Same tab look as the history and Operations screens.
  tabStrip: { flexDirection: 'row', paddingHorizontal: 20, height: 44 },
  tabButton: { flex: 1, alignItems: 'center', justifyContent: 'flex-end' },
  tabLabel: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '400',
    color: COLORS.blueSteel,
    paddingBottom: 8,
  },
  tabLabelActive: { color: COLORS.white, fontWeight: '500' },
  tabUnderline: { height: 1, width: '100%', backgroundColor: COLORS.blueSteel },
  tabUnderlineActive: { height: 2, backgroundColor: COLORS.brandOrange },
  contentWrapper: {
    flex: 1,
    backgroundColor: COLORS.pureLight,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    marginTop: 10,
    overflow: 'hidden',
  },
  content: { padding: 16, paddingBottom: 130, gap: 16 },
  banner: { width: '100%', borderRadius: 12, overflow: 'hidden' },
  bannerImage: { ...StyleSheet.absoluteFillObject, width: '100%', height: '100%' },
  bannerText: { position: 'absolute', top: '10%', left: '5%', right: '35%' },
  // The T-FUND caption breaks where the design breaks it, so the block has
  // to be wide enough that no line wraps again. The first three lines end
  // left of the shield; the longest one runs below it.
  bannerTextWide: { right: '8%' },
  bannerTitle: { fontFamily: 'Inter', fontSize: 22, fontWeight: '500', color: COLORS.white, marginBottom: 6 },
  bannerSubtitle: { fontFamily: 'Inter', fontSize: 13, lineHeight: 18, color: 'rgba(255,255,255,0.85)' },
  apyBadge: {
    position: 'absolute',
    left: '5%',
    bottom: '10%',
    backgroundColor: COLORS.white,
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  apyCurrency: { fontFamily: 'Inter', fontSize: 9, color: COLORS.blueSteel },
  apyValue: { fontFamily: 'Inter', fontSize: 20, fontWeight: '600', color: COLORS.primaryNavy },
  disclaimer: {
    position: 'absolute',
    right: '5%',
    bottom: '10%',
    textAlign: 'right',
    fontFamily: 'Inter',
    fontSize: 11,
    lineHeight: 14,
    color: 'rgba(255,255,255,0.75)',
  },
  infoCard: { ...CARD_SURFACE, paddingHorizontal: 16, paddingVertical: 2 },
  rowDivider: { borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: COLORS.borderMuted },
  featureRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 12 },
  featureIcon: { width: 20, height: 20, marginRight: 12, tintColor: COLORS.steelNavy },
  featureLabel: { flex: 1, fontFamily: 'Inter', fontSize: 14, color: COLORS.primaryNavy },
  featureValue: { fontFamily: 'Inter', fontSize: 14, fontWeight: '500', color: COLORS.brandOrange },
  rateRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 12 },
  rateHead: { fontSize: 12, fontWeight: '400', color: COLORS.blueSteel },
  rateTerm: { flex: 1.4, fontFamily: 'Inter', fontSize: 14, color: COLORS.primaryNavy },
  rateCell: { flex: 1, textAlign: 'right', fontFamily: 'Inter', fontSize: 14, fontWeight: '500', color: COLORS.primaryNavy },
  button: {
    height: 48,
    borderRadius: 8,
    backgroundColor: COLORS.primaryNavy,
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonDisabled: { opacity: 0.5 },
  buttonText: { fontFamily: 'Inter', fontSize: 16, lineHeight: 20, fontWeight: '500', color: COLORS.white },
});

export default ProductsScreen;
